import { MOBILE_PORTRAIT_WIDTH } from '../Observodon/constants';

/**
 * Returns a promise that is resolved with a value indicating whether the video
 * file satisfies the maximum duration limit. Returns TRUE if the video can't
 * be loaded by the browser's `<video>` player.
 * @param file Video file whose duration to check.
 * @param maxDuration Maximum duration, in seconds.
 */
export const checkVideoDuration = async (
  file: File,
  maxDuration: number
): Promise<boolean> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    if (['maybe', 'yes'].includes(video.canPlayType(file.type))) {
      const url = URL.createObjectURL(file);
      video.src = url;
      let int: NodeJS.Timer, tmr: NodeJS.Timeout;

      // Give some time for video element to load duration.
      tmr = setTimeout(() => {
        clearInterval(int);
        resolve(true);
      }, 5000);

      // Poll the video element to see whether the duration has loaded.
      // Some screen recorded videos report duration Infinity.
      int = setInterval(() => {
        const duration = video.duration;
        if (!isNaN(duration) && duration !== Infinity) {
          clearTimeout(tmr);
          clearInterval(int);
          resolve(duration < maxDuration);
        }
      }, 200);
      return;
    }
    // If we can't check the video, assume it's ok.
    resolve(true);
  });
};

/**
 * Create a File object from the blob.
 * @param blob Blob content.
 * @param name Filename to give to the blob.
 * @param type MIME type of content, default application/pdf.
 */
export const blobToFile = (
  blob: Blob,
  name: string,
  type: string = 'application/pdf'
) => {
  return new File([blob], name, { type });
};

/**
 * Open the given file in a new window.
 * @param file File to open, e.g. from blobToFile.
 */
export const openFileInWindow = (file: File) => {
  const url = URL.createObjectURL(file);
  window.open(url);
};

/**
 * Trigger the file to download.
 * @param file File to download, e.g. from blobToFile.
 */
export const downloadFile = (file: File) => {
  const link = document.createElement('a');
  if (link) {
    const url = window.URL.createObjectURL(file);
    link.href = url;
    link.setAttribute('download', file.name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    throw new Error('Failed to download a file with the requested data.');
  }
};

/**
 * Returns a value indicating whether the page is running on a touch device,
 * which could be a mobile phone or tablet. These devices often don't emit
 * events such as video.canplay or window.open, unless these commands are
 * triggered synchronously to a user action event.
 * See https://stackoverflow.com/a/72878777.
 */
export const detectTouchDevice = (): boolean => {
  try {
    document.createEvent('TouchEvent');
    return true;
  } catch (_) {
    // NOP - not a touch device
  }
  return false;
};

/**
 * Simple converter from Markdown-ish text to HTML.
 * @param md Markdown-ish text to convert.
 */
export const mdToHtml = (md: string | undefined): string => {
  const repl: { [key: string]: string } = {
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;'
  };
  return (md || '')
    .replace(new RegExp(Object.keys(repl).join('|'), 'g'), (m) => repl[m] || m)
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>');
};

/**
 * Create a regular expression matching the search text.
 */
export const searchExpression = (searchText: string): RegExp => {
  return new RegExp(
    // Regular expression matching the search text, with regex special
    // characters escaped, case insensitive.
    searchText?.replace(/([*?.+$\/{}()\[\]\\])/g, '\\$1'),
    'i'
  );
};

export const downsizeImage = async (
  base64: string,
  maxWidth = 1280,
  quality = 0.85
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');

      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const resizedBase64 = canvas.toDataURL('image/jpeg', quality);
      resolve(resizedBase64);
    };

    img.onerror = reject;
    img.src = base64;
  });
};

export const resizeImageByWidth = (
  file: File,
  targetWidth: number = 1280,
  quality = 1
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const scale = targetWidth / img.width;
      const targetHeight = Math.round(img.height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Canvas not supported');

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject('Blob conversion failed');

          resolve(
            new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            })
          );
        },
        file.type,
        quality
      );
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export const isMobile = (windowWidth: number, windowHeight: number) => {
  return Math.min(windowWidth, windowHeight) < MOBILE_PORTRAIT_WIDTH;
};

export const isMobileLandscape = (windowHeight: number) => {
  return windowHeight < MOBILE_PORTRAIT_WIDTH;
};
